import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import times from 'lodash/times'
import { useQuery } from 'react-query'
import type { ListOnScrollProps } from 'react-window'
import { VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button.tsx'
import { SearchInput } from '@/components/ui/search-input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip } from '@/components/ui/tooltip'
import { DEFAULT_SELECTED_LIST, UNREAD_LIST } from '@/consts/chats.ts'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import {
  $canLoadNextChatPages,
  $chatListError,
  $chats,
  $selectedList,
  getChatsFx,
  loadNextChatsPage,
  resetChats,
  setQuery,
  setSelectedList,
} from '@/models/chattingUI/chats/chats.ts'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasMassMessagesPermission } from '@/models/permissions.ts'
import { useCountdowns } from '@/modules/chatting/state/countdown-provider.tsx'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { handleError } from '@/utils/error'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { classNames } from '@/utils/helpers'

import { useLists } from '../../hooks/use-lists'
import { usePinnedLists } from '../../hooks/use-pinned-lists'
import BirthdayIcon from '../../icons/birthday.svg'
import ChevronDownIcon from '../../icons/chevron-down.svg'
import CollectionsIcon from '../../icons/collections.svg'
import NewMessageIcon from '../../icons/new-message.svg'
import SidebarCollapseIcon from '../../icons/sidebar-collapse.svg'
import SidebarExpandIcon from '../../icons/sidebar-expand.svg'
import StatisticIcon from '../../icons/statistic.svg'
import { chatsService } from '../../service/chats'
import { IconButton } from '../components/icon-button'
import { TagList } from '../components/tags-list'
import { ChattingSidebarProps } from './ChattingSidebar'
import { ChatInfo } from './chat-info'
import type { FloatingChatInfoConfig } from './chat-item'
import { ChatItemRow } from './chat-item/ChatItemRow'
import { ChatItemSkeleton } from './chat-item/skeleton'
import { CollapsedSidebarActions } from './collapsed-sidebar-actions'
import { EditPinnedLists } from './edit-pinned-lists'
import type { ListsDropdownRef } from './lists-dropdown'
import { ListsDropdown } from './lists-dropdown'
import { MessageQueueList } from './message-queue-list'
import type { MoreActionsRef } from './more-actions'
import { MoreActions } from './more-actions'
import { UpcomingBirthdays } from './upcoming-birthdays'
import { useSizeObserver } from './use-size-observer'

export interface SidebarContentProps extends ChattingSidebarProps {
  isExpanded: boolean
  setIsExpanded: Dispatch<SetStateAction<boolean>>
}

export const SidebarContent = ({
  isMobileSidebarOpen,
  closeMobileSidebar,
  isExpanded,
  setIsExpanded,
}: SidebarContentProps) => {
  const creatorId = useUnit($creatorId)
  const creator = useUnit($currentCreator)
  const hasMassMessagesPermission = useUnit($hasMassMessagesPermission)
  const { countdowns } = useCountdowns()
  const lastVisibleIndexRef = useRef<number | null>(null)
  const isLoading = useUnit(getChatsFx.pending)
  const selectedList = useUnit($selectedList)
  const canLoadMore = useUnit($canLoadNextChatPages)
  const chats = useUnit($chats)
  const chatListError = useUnit($chatListError)

  const [parentRef, width, height] = useSizeObserver()
  const [isUpcomingBirthdaysDialogOpen, setIsUpcomingBirthdaysDialogOpen] =
    useState(false)
  const searchFieldRef = useRef<HTMLInputElement | null>(null)
  const listDropdownRef = useRef<ListsDropdownRef | null>(null)
  const moreActionsRef = useRef<MoreActionsRef | null>(null)
  const [floatingChatInfoConfig, setFloatingChatInfoConfig] =
    useState<FloatingChatInfoConfig | null>(null)
  const upcomingBirthdaysQuery = useQuery(
    ['upcoming-birthdays', creatorId],
    () => chatsService.getUpcomingBirthdays(creatorId!),
    {
      refetchInterval: 3 * 60 * 1000,
      enabled: !!creatorId,
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while getting upcoming birthdays',
          upcomingBirthdaysQuery.refetch,
        )
      },
    },
  )
  const unreadChatsCountQuery = useQuery(
    ['unreadChatsCount', creatorId],
    () => chatsService.getUnreadChatsCount(creatorId!),
    {
      enabled: !!creatorId,
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while getting unread chats count',
          unreadChatsCountQuery.refetch,
        )
      },
    },
  )

  const pinnedLists = useLists('pinned-lists')
  const { isPinning, onSelectList } = usePinnedLists()
  const [allListsQueryEnabled, setAllListsQueryEnabled] = useState(false)
  const allLists = useLists('all-lists', { enabled: allListsQueryEnabled })

  const disabledListIds = useMemo(
    () =>
      allLists.editableLists
        .filter(({ usersCount }) => !usersCount)
        .map((list) => list.id),
    [allLists.editableLists],
  )

  const handleSelectList = useCallback(
    async (listId: string) => {
      const isPinned = pinnedLists.lists.some((list) => list.id === listId)

      await onSelectList(listId, isPinned)
      setSelectedList(listId)
    },
    [onSelectList, pinnedLists.lists],
  )

  const onSelectChat = useCallback(() => {
    if (isMobileSidebarOpen) {
      closeMobileSidebar()
    }
  }, [isMobileSidebarOpen, closeMobileSidebar])

  const getItemSize = useCallback(() => 82, [])

  const handleClickCollapsedLists = useCallback(() => {
    setIsExpanded(true)
    setTimeout(() => {
      if (listDropdownRef.current) {
        listDropdownRef.current.open()
      }
    }, 200)
  }, [setIsExpanded])

  const handleClickCollapsedSearch = useCallback(() => {
    setIsExpanded(true)
    setTimeout(() => {
      if (searchFieldRef.current) {
        searchFieldRef.current.focus()
      }
    }, 200)
  }, [setIsExpanded])

  const handleClickCollapsedMore = useCallback(() => {
    setIsExpanded(true)
    setTimeout(() => {
      if (moreActionsRef.current) {
        moreActionsRef.current.open()
      }
    }, 200)
  }, [setIsExpanded])

  const handleOpenBirthdaysDialog = useCallback(() => {
    if (!isEmpty(upcomingBirthdaysQuery.data?.days)) {
      setIsUpcomingBirthdaysDialogOpen(true)
    }
  }, [upcomingBirthdaysQuery.data])

  const handleCloseBirthdaysDialog = useCallback(() => {
    setIsUpcomingBirthdaysDialogOpen(false)
  }, [])

  const handleMMStatsClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.MASS_MESSAGE_STATISTICS,
      isPersistent: true,
    })
  }, [])

  const handleNewMassMessageClick = useCallback(() => {
    if (hasMassMessagesPermission) {
      openDialog({
        type: DIALOG_TYPE.NEW_MESSAGE,
        isPersistent: true,
      })
    } else {
      openDialog({
        type: DIALOG_TYPE.FANS_LIST_TO_START_CHAT_WITH,
      })
    }
  }, [hasMassMessagesPermission])

  const openCollectionsDialog = () => {
    trackFeatureUsage(TrackedFeatures.CUICollections, creatorId)
    openDialog({
      type: DIALOG_TYPE.COLLECTIONS,
      isPersistent: true,
    })
  }

  const birthdaysCount = Object.values(
    upcomingBirthdaysQuery.data?.days ?? {},
  ).flatMap((bdays) => bdays).length

  useEffect(() => {
    // If scroll cannot be triggered
    if (creatorId && getItemSize() * chats.length <= height && canLoadMore) {
      loadNextChatsPage()
    }
  }, [height, chats.length, getItemSize, canLoadMore, creatorId])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    debounce(({ scrollDirection, scrollOffset }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== chats.length - 1 ||
        !canLoadMore
      ) {
        return
      }

      if (scrollDirection === 'forward' && scrollOffset > 0) {
        loadNextChatsPage()
      }
    }, 100),
    [canLoadMore, chats.length],
  )
  const unreadChatsCount = unreadChatsCountQuery.data
  const unreadChatList = { ...UNREAD_LIST, badge: unreadChatsCount }

  if (!creator) return null

  return (
    <>
      {isExpanded ? (
        <div className="space-y-4 p-4">
          <div className="flex items-center gap-3">
            <CreatorAvatar
              name={creator.name}
              avatarSrc={creator.avatar?.croppedUrl}
              className="h-8 w-8"
            />
            <span className="font-medium">{creator.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xl font-semibold">
              <Tooltip
                content="Refresh"
                asChild
              >
                <span
                  onClick={resetChats}
                  className="cursor-pointer"
                >
                  Unread Chats
                </span>
              </Tooltip>
              <span className="text-secondary-200">&#x2022;</span>
              <span className="text-primary-500">
                {unreadChatsCountQuery.isLoading ? (
                  <Skeleton className="dark:bg-skeleton h-[28px] w-5" />
                ) : (
                  unreadChatsCountQuery.data
                )}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <IconButton
                tooltip={
                  hasMassMessagesPermission
                    ? 'New Mass Message'
                    : 'OnlyFans - Mass Message permission required'
                }
                disabled={!hasMassMessagesPermission}
                icon={NewMessageIcon}
                iconClassName="text-primary-500"
                onClick={handleNewMassMessageClick}
              />
              <IconButton
                tooltip="Collections"
                icon={CollectionsIcon}
                onClick={openCollectionsDialog}
              />
              <IconButton
                tooltip="Minimize"
                icon={SidebarCollapseIcon}
                onClick={() => setIsExpanded(false)}
              />
            </div>
          </div>
          <div className="text-secondary-300 flex items-center gap-3">
            <SearchInput
              ref={searchFieldRef}
              placeholder="Search"
              className="resize-none"
              onInputChange={setQuery}
            />
            {/*<IconButton tooltip="Queue" icon={CalendarIcon} />*/}
            {hasMassMessagesPermission && (
              <IconButton
                tooltip="Mass Message Statistics"
                icon={StatisticIcon}
                onClick={handleMMStatsClick}
              />
            )}
            <div className="relative flex items-center">
              <IconButton
                tooltip={
                  isEmpty(upcomingBirthdaysQuery.data?.days)
                    ? "There are no fans' birthdays in the next couple of days."
                    : 'Birthday'
                }
                icon={BirthdayIcon}
                onClick={handleOpenBirthdaysDialog}
                disabled={
                  upcomingBirthdaysQuery.isLoading ||
                  isEmpty(upcomingBirthdaysQuery.data?.days)
                }
              />
              {birthdaysCount > 0 && (
                <div className="bg-danger-default absolute -right-2.5 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full text-center text-xs leading-4 text-white pointer-events-none">
                  {birthdaysCount}
                </div>
              )}
            </div>
            <MoreActions ref={moreActionsRef} />
          </div>
          <TagList
            isLoading={pinnedLists.isLoading}
            list={[DEFAULT_SELECTED_LIST, unreadChatList, ...pinnedLists.lists]}
            selectedItems={[selectedList]}
            onClickItem={handleSelectList}
            listClassName="max-w-[calc(100%-80px)]"
            canLoadMore={pinnedLists.canLoadMore}
            onLoadMore={pinnedLists.onLoadMore}
            disabledItems={disabledListIds}
            isLoadingMore={pinnedLists.isFetchingNextPage}
            actions={
              <>
                <ListsDropdown
                  onOpen={() => {
                    setAllListsQueryEnabled(true)
                  }}
                  disabled={pinnedLists.isLoading}
                  ref={listDropdownRef}
                  isLoadingMore={
                    allLists.isFetchingNextPage || allLists.isLoading
                  }
                  disabledItems={disabledListIds}
                  onSelect={handleSelectList}
                  items={[
                    DEFAULT_SELECTED_LIST,
                    UNREAD_LIST,
                    ...allLists.editableLists,
                  ]}
                  canLoadMore={allLists.canLoadMore}
                  onLoadMore={allLists.onLoadMore}
                  trigger={({ isOpen }) => (
                    <IconButton
                      disabled={pinnedLists.isLoading}
                      icon={ChevronDownIcon}
                      tooltip="All lists"
                      rounded
                      iconClassName={classNames(
                        'transition-transform',
                        isOpen && '-rotate-180 text-primary-500',
                      )}
                      className={classNames(
                        isOpen && 'bg-primary-50 text-title',
                      )}
                    />
                  )}
                />
                <EditPinnedLists isTriggerDisabled={pinnedLists.isLoading} />
              </>
            }
          />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 p-4">
          <IconButton
            className="text-title h-6 w-6"
            onClick={() => setIsExpanded(true)}
            icon={SidebarExpandIcon}
            tooltip="Maximize"
          />
          <CollapsedSidebarActions
            onClickLists={handleClickCollapsedLists}
            onClickSearch={handleClickCollapsedSearch}
            onClickMore={handleClickCollapsedMore}
            onClickMassMessagesStats={handleMMStatsClick}
            onClickCollections={openCollectionsDialog}
            onClickBirthdays={handleOpenBirthdaysDialog}
          />
        </div>
      )}
      <MessageQueueList />
      <div
        className="relative max-h-full w-full flex-1 overflow-hidden"
        ref={parentRef}
      >
        {!isLoading && chats.length === 0 && (
          <div className="flex mt-10 flex-col items-center justify-center gap-2">
            <NotFoundImage
              width={120}
              height={120}
            />
            {chatListError ? (
              <span className="text-center text-sm font-medium text-secondary-300">
                <span className="text-center text-sm font-medium text-secondary-300">
                  Chats cannot be loaded.
                  <br />
                  Please try again later.
                </span>
                <Button
                  className="mt-3 w-1/2"
                  size="sm"
                  onClick={resetChats}
                >
                  Retry
                </Button>
              </span>
            ) : (
              <span className="text-center text-sm font-medium text-secondary-300">
                No chats found
              </span>
            )}
          </div>
        )}
        {(isLoading && !chats.length) || isPinning ? (
          times(5).map((num) => (
            <ChatItemSkeleton
              key={num}
              isSidebarExpanded={isExpanded}
            />
          ))
        ) : (
          <VariableSizeList
            className="scrollbar-hide overscroll-none"
            onScroll={handleScroll}
            itemData={{
              items: chats,
              onSelectChat: onSelectChat,
              setFloatingChatInfoConfig: setFloatingChatInfoConfig,
              isExpanded: isExpanded,
              setIndex: (index) => {
                lastVisibleIndexRef.current = index
              },
            }}
            itemSize={getItemSize}
            height={height}
            itemCount={chats.length}
            width={width}
          >
            {ChatItemRow}
          </VariableSizeList>
        )}
        {isLoading && chats.length > 0 && (
          <div className="bg-card absolute bottom-0 left-0 right-0">
            <IconLoading className="mx-auto my-3 animate-spin" />
          </div>
        )}
      </div>
      {floatingChatInfoConfig && (
        <div
          className={classNames(
            'fixed z-10 -translate-y-1/2 pl-2',
            !floatingChatInfoConfig.visible && 'hidden hover:block',
          )}
          style={floatingChatInfoConfig.position}
        >
          <div
            className="bg-card rounded-md p-2"
            style={floatingChatInfoConfig.position}
          >
            <ChatInfo
              key={floatingChatInfoConfig.chat.withUser.id}
              chat={floatingChatInfoConfig?.chat}
              countdown={countdowns[floatingChatInfoConfig?.chat.withUser.id]}
            />
          </div>
        </div>
      )}
      <UpcomingBirthdays
        isOpen={isUpcomingBirthdaysDialogOpen}
        onClose={handleCloseBirthdaysDialog}
        birthdays={upcomingBirthdaysQuery.data?.days}
      />
    </>
  )
}

SidebarContent.whyDidYouRender = true
